import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createVNode as _createVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, vShow as _vShow, normalizeClass as _normalizeClass, withDirectives as _withDirectives, mergeProps as _mergeProps } from "vue"

const _hoisted_1 = { class: "form" }
const _hoisted_2 = { class: "form-header" }
const _hoisted_3 = {
  class: "title",
  ref: "title"
}
const _hoisted_4 = { class: "tooltip-wrapper" }
const _hoisted_5 = { class: "actions" }
const _hoisted_6 = ["disabled"]
const _hoisted_7 = ["disabled"]
const _hoisted_8 = { class: "address-formatted" }
const _hoisted_9 = {
  class: "form-fields",
  id: "address"
}
const _hoisted_10 = { class: "row" }
const _hoisted_11 = { class: "col-12 col-mobile-12" }
const _hoisted_12 = {
  class: "col-group",
  style: {}
}
const _hoisted_13 = { class: "col-lg-6 col-md-6 col-mobile-12" }
const _hoisted_14 = { class: "col-lg-6 col-md-6 col-mobile-12" }
const _hoisted_15 = { class: "col-group" }
const _hoisted_16 = { class: "col-lg-6 col-md-6 col-mobile-12" }
const _hoisted_17 = { class: "col-lg-6 col-md-6 col-mobile-12" }
const _hoisted_18 = { class: "col-group" }
const _hoisted_19 = { class: "col-lg-6 col-md-6 col-mobile-12" }
const _hoisted_20 = { class: "col-lg-6 col-md-6 col-mobile-12" }
const _hoisted_21 = ["disabled"]

import {
  computed,
  toRefs,
  ref,
  onMounted,
  watch,
  onUnmounted,
} from "vue";
import { FieldSchema, Option } from "@/models/form";
import BaseFormField from "@/components/shared/BaseFormField.vue";
import { useStore } from "vuex";
import NextButton from "@/components/shared/NextButton.vue";
import { Applicant, Address, AddressFormInputs } from "@/models/application";
import { Stipulation } from "@/models/opal";
import { FormModes } from "@/models/form";
import { Field as VeeField, ErrorMessage, configure } from "vee-validate";
import useAddressForm, { formatAddress } from "@/composables/forms/useAddressForm";
import SlideTransition from "@/components/transitions/SlideTransition.vue";
import TooltipComponent from "@/components/shared/TooltipComponent.vue";
import Icon from "@/components/shared/Icon.vue";

interface Props {
  address: AddressFormInputs;
  addresses?: any[];
  previousAddresses?: any[];
  applicant: Applicant;
  stipulation: Stipulation;
  index: number;
  disabled: boolean;
  loading: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AddressForm',
  props: {
    address: {},
    addresses: {},
    previousAddresses: {},
    applicant: {},
    stipulation: {},
    index: {},
    disabled: { type: Boolean, default: false },
    loading: { type: Boolean }
  },
  emits: ["closeDialogueModal", "onDelete", "onError"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const store = useStore();

const props = __props;

const emit = __emit;

configure({
  // Stops validation when model value changes (input)
  validateOnModelUpdate: true,
  // Prevents validation when the component is first mounted
  validateOnInput: true,
  // Optionally turn off blur/change if needed
  validateOnBlur: true,
  validateOnChange: true,
});

const { address, previousAddresses } = toRefs(props);

const isMounted = ref<boolean>(false);

const isFormDisabled = computed(() => {
  return props.disabled || mode.value === FormModes.view;
});

const {
  addressSchema,
  countries,
  error,
  errors,
  formattedAddress,
  getOption,
  mode,
  states,
  unitTypes,
  resetForm,
  resetAddressSchema,
  onValidate,
  savingChanges,
  saveAddressAsync,
  showAddressDetails,
  showFields,
  statesData,
  values,
  validate,
  validating,
} = useAddressForm(
  {
    formData: address,
    disabled: isFormDisabled,
    previousAddresses,
    isMounted,
  },
  store,
);

const canEdit = computed<boolean>(() => {
  return !props.disabled || !savingChanges.value || mode.value !== FormModes.view;
});

const hasErrors = computed(() => Object.keys(errors.value)?.length > 0);

async function handleChange(event: number | string, schema: FieldSchema, options?: Option[]) {
  const value = event;
  const option = options ? getOption(value, options) : undefined;

  switch (schema.name) {
    case "addressUnitTypeId":
      address.value.addressUnitTypeName = option?.label;
      break;
    case "stateId":
      address.value.stateName = option?.label;
      address.value.stateAbbreviation = statesData.value.find(
        (s: any) => s.id === value,
      )?.abbreviation;
      break;
    case "countryId":
      address.value.countryName = option?.label;
      break;
    default:
      break;
  }
}

function handleEditOrDelete(address: AddressFormInputs, index: number) {
  if (canEdit.value) {
    if (mode.value === FormModes.view) {
      changeMode(FormModes.edit);
    } else {
      deleteAddress(address, index);
    }
  }
}

function deleteAddress(address: AddressFormInputs, index: number) {
  emit("onDelete", {
    index,
    address,
  });
}

function changeMode(newMode: keyof typeof FormModes) {
  address.value.mode = newMode;
  mode.value = newMode;
}

function updateAddressId(updatedAddress: AddressFormInputs) {
  address.value.id = updatedAddress.id || updatedAddress.addressId;
  address.value.addressId = updatedAddress.id || updatedAddress.addressId;
}

async function confirmChanges() {
  validating.value = true;
  error.value = false;

  try {		
    const validationResponse = await onValidate();

    if (validationResponse.valid) {
      await saveChanges();
    } else {
      validating.value = false;
    }
  } catch (error: any) {
    console.log("Validation failed. Errors:", (error as any)?.errors);
    validating.value = false;
    error.value = true;
  }
}

async function saveChanges() {
  savingChanges.value = true;

  try {
    const response = await saveAddressAsync();
    if (response?.success) {
      updateAddressId(response.results);
      changeMode(FormModes.view);
      showAddressDetails.value = false;
      emit("closeDialogueModal");
      savingChanges.value = false;
    } else {
      emit("onError");
      savingChanges.value = false;
    }
  } catch {
    savingChanges.value = false;
    error.value = true;
  }
}

onMounted(() => {
  mode.value = props.address.mode;
  isMounted.value = true;
});

onUnmounted(() => {
  resetForm();
  mode.value = FormModes.view;
});
watch(
  () => mode.value,
  newValue => {
    if (newValue === FormModes.view) {
      resetAddressSchema();
    }
  },
);

__expose({
  address,
  confirmChanges,
  error,
  formattedAddress,
  hasErrors,
  validate,
  validating,
  props,
  saveChanges,
  showAddressDetails,
});

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["form-wrapper", [_unref(mode)]])
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          _createTextVNode(" Address " + _toDisplayString(__props.index + 1) + " ", 1),
          _createElementVNode("div", _hoisted_4, [
            (_unref(address).id > 0 && _unref(mode) === _unref(FormModes).view)
              ? (_openBlock(), _createBlock(TooltipComponent, { key: 0 }, {
                  toggle: _withCtx(() => [
                    _cache[13] || (_cache[13] = _createTextVNode(" ", -1)),
                    _createVNode(Icon, { icon: "info" })
                  ]),
                  default: _withCtx(() => [
                    _cache[14] || (_cache[14] = _createElementVNode("div", null, " This address has already been saved and cannot be deleted. You can still make edits by clicking \"edit\". ", -1))
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true)
          ])
        ], 512),
        _createElementVNode("div", _hoisted_5, [
          (_unref(address).id <= 0)
            ? (_openBlock(), _createElementBlock("a", {
                key: 0,
                disabled: __props.disabled,
                onClick: _cache[0] || (_cache[0] = ($event: any) => (handleEditOrDelete(_unref(address), __props.index)))
              }, _toDisplayString(_unref(mode) === _unref(FormModes).view ? "edit" : "delete"), 9, _hoisted_6))
            : _createCommentVNode("", true),
          (_unref(address).id > 0 && _unref(mode) === _unref(FormModes).view)
            ? (_openBlock(), _createElementBlock("a", {
                key: 1,
                disabled: __props.disabled,
                onClick: _cache[1] || (_cache[1] = ($event: any) => (changeMode(_unref(FormModes).edit)))
              }, "edit", 8, _hoisted_7))
            : _createCommentVNode("", true)
        ])
      ]),
      _withDirectives(_createElementVNode("div", {
        class: _normalizeClass(["summary", { isCollapsed: _unref(mode) === _unref(FormModes).view && !_unref(showAddressDetails) }])
      }, [
        _createElementVNode("span", _hoisted_8, _toDisplayString(_unref(formattedAddress)), 1),
        _createElementVNode("div", {
          class: "details-toggle",
          onClick: _cache[2] || (_cache[2] = ($event: any) => (showAddressDetails.value = !_unref(showAddressDetails)))
        }, [
          _createTextVNode(_toDisplayString(_unref(showAddressDetails) ? "Hide address details" : "View address details") + " ", 1),
          _createVNode(_component_font_awesome_icon, {
            icon: ['fa-solid', _unref(showAddressDetails) ? 'fa-angle-up' : 'fa-angle-down'],
            class: "icon"
          }, null, 8, ["icon"])
        ])
      ], 2), [
        [_vShow, _unref(mode) === _unref(FormModes).view]
      ]),
      _createVNode(SlideTransition, { "is-visible": _unref(showFields) }, {
        default: _withCtx(() => [
          _withDirectives(_createElementVNode("div", _hoisted_9, [
            _createElementVNode("div", _hoisted_10, [
              _createElementVNode("div", _hoisted_11, [
                _createVNode(_unref(VeeField), {
                  name: "address1",
                  value: _unref(address).address1
                }, {
                  default: _withCtx(({ field }) => [
                    _createVNode(BaseFormField, _mergeProps({
                      modelValue: _unref(address).address1,
                      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((_unref(address).address1) = $event))
                    }, field, {
                      schema: _unref(addressSchema).address1,
                      disabled: !canEdit.value || isFormDisabled.value
                    }), {
                      errors: _withCtx(() => [
                        _withDirectives(_createVNode(_unref(ErrorMessage), {
                          name: "address1",
                          class: "error"
                        }, null, 512), [
                          [_vShow, _unref(mode) !== 'view']
                        ])
                      ]),
                      _: 1
                    }, 16, ["modelValue", "schema", "disabled"])
                  ]),
                  _: 1
                }, 8, ["value"])
              ])
            ]),
            _createElementVNode("div", _hoisted_12, [
              _createElementVNode("div", _hoisted_13, [
                _createVNode(_unref(VeeField), {
                  name: "addressUnitTypeId",
                  value: _unref(address).addressUnitTypeId
                }, {
                  default: _withCtx(({ field }) => [
                    _createVNode(BaseFormField, _mergeProps({
                      modelValue: _unref(address).addressUnitTypeId,
                      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((_unref(address).addressUnitTypeId) = $event)),
                      modelModifiers: { number: true }
                    }, field, {
                      schema: _unref(addressSchema).addressUnitTypeId,
                      options: _unref(unitTypes),
                      disabled: !canEdit.value || isFormDisabled.value,
                      class: {
                    'is-invalid':
                      !_unref(addressSchema).addressUnitTypeId.valid && _unref(errors)?.addressUnitTypeId,
                  },
                      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => (
                    handleChange($event, _unref(addressSchema).addressUnitTypeId, _unref(unitTypes))
                  ))
                    }), {
                      errors: _withCtx(() => [
                        _withDirectives(_createVNode(_unref(ErrorMessage), {
                          name: "addressUnitTypeId",
                          class: "error"
                        }, null, 512), [
                          [_vShow, _unref(mode) !== 'view']
                        ])
                      ]),
                      _: 1
                    }, 16, ["modelValue", "schema", "options", "disabled", "class"])
                  ]),
                  _: 1
                }, 8, ["value"])
              ]),
              _createElementVNode("div", _hoisted_14, [
                _createVNode(_unref(VeeField), {
                  name: "address2",
                  value: _unref(address).address2
                }, {
                  default: _withCtx(({ field }) => [
                    _createVNode(BaseFormField, _mergeProps({
                      modelValue: _unref(address).address2,
                      "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((_unref(address).address2) = $event))
                    }, field, {
                      autocomplete: "address-line2",
                      schema: _unref(addressSchema).address2,
                      disabled: !canEdit.value || isFormDisabled.value,
                      class: {
                    'is-invalid': !_unref(addressSchema).address2.valid && _unref(errors)?.address2,
                  }
                    }), {
                      errors: _withCtx(() => [
                        _withDirectives(_createVNode(_unref(ErrorMessage), {
                          name: "address2",
                          class: "error"
                        }, null, 512), [
                          [_vShow, _unref(mode) !== 'view']
                        ])
                      ]),
                      _: 1
                    }, 16, ["modelValue", "schema", "disabled", "class"])
                  ]),
                  _: 1
                }, 8, ["value"])
              ])
            ]),
            _createElementVNode("div", _hoisted_15, [
              _createElementVNode("div", _hoisted_16, [
                _createVNode(_unref(VeeField), {
                  name: "city",
                  value: _unref(address).city
                }, {
                  default: _withCtx(({ field }) => [
                    _createVNode(BaseFormField, _mergeProps({
                      modelValue: _unref(address).city,
                      "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((_unref(address).city) = $event))
                    }, field, {
                      schema: _unref(addressSchema).city,
                      disabled: !canEdit.value || isFormDisabled.value,
                      class: {
                    'is-invalid': !_unref(addressSchema).city.valid && _unref(errors)?.city,
                  }
                    }), {
                      errors: _withCtx(() => [
                        _withDirectives(_createVNode(_unref(ErrorMessage), {
                          name: "city",
                          class: "error"
                        }, null, 512), [
                          [_vShow, _unref(mode) !== 'view']
                        ])
                      ]),
                      _: 1
                    }, 16, ["modelValue", "schema", "disabled", "class"])
                  ]),
                  _: 1
                }, 8, ["value"])
              ]),
              _createElementVNode("div", _hoisted_17, [
                _createVNode(_unref(VeeField), {
                  name: "stateId",
                  value: _unref(address).stateId
                }, {
                  default: _withCtx(({ field }) => [
                    _createVNode(BaseFormField, _mergeProps({
                      modelValue: _unref(address).stateId,
                      "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((_unref(address).stateId) = $event)),
                      modelModifiers: { number: true }
                    }, field, {
                      schema: _unref(addressSchema).stateId,
                      options: _unref(states),
                      disabled: !canEdit.value || isFormDisabled.value,
                      class: {
                    'is-invalid': !_unref(addressSchema).stateId.valid && _unref(errors)?.stateId,
                  },
                      "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => (handleChange($event, _unref(addressSchema).stateId, _unref(states))))
                    }), {
                      "default-option": _withCtx(() => [...(_cache[15] || (_cache[15] = [
                        _createTextVNode(" State ", -1)
                      ]))]),
                      errors: _withCtx(() => [
                        _withDirectives(_createVNode(_unref(ErrorMessage), {
                          name: "stateId",
                          class: "error"
                        }, null, 512), [
                          [_vShow, _unref(mode) !== 'view']
                        ])
                      ]),
                      _: 1
                    }, 16, ["modelValue", "schema", "options", "disabled", "class"])
                  ]),
                  _: 1
                }, 8, ["value"])
              ])
            ]),
            _createElementVNode("div", _hoisted_18, [
              _createElementVNode("div", _hoisted_19, [
                _createVNode(_unref(VeeField), {
                  name: "postalCode",
                  value: _unref(address).postalCode
                }, {
                  default: _withCtx(({ field }) => [
                    _createVNode(BaseFormField, _mergeProps({
                      modelValue: _unref(address).postalCode,
                      "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((_unref(address).postalCode) = $event))
                    }, field, {
                      schema: _unref(addressSchema).postalCode,
                      disabled: !canEdit.value || isFormDisabled.value,
                      class: {
                    'is-invalid': !_unref(addressSchema).postalCode.valid && _unref(errors)?.postalCode,
                  }
                    }), {
                      errors: _withCtx(() => [
                        _withDirectives(_createVNode(_unref(ErrorMessage), {
                          name: "postalCode",
                          class: "error"
                        }, null, 512), [
                          [_vShow, _unref(mode) !== 'view']
                        ])
                      ]),
                      _: 1
                    }, 16, ["modelValue", "schema", "disabled", "class"])
                  ]),
                  _: 1
                }, 8, ["value"])
              ]),
              _createElementVNode("div", _hoisted_20, [
                _createVNode(_unref(VeeField), {
                  name: "countryId",
                  value: _unref(address).countryId
                }, {
                  default: _withCtx(({ field }) => [
                    _createVNode(BaseFormField, _mergeProps({
                      modelValue: _unref(address).countryId,
                      "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((_unref(address).countryId) = $event)),
                      modelModifiers: { number: true }
                    }, field, {
                      schema: _unref(addressSchema).countryId,
                      options: _unref(countries),
                      disabled: !canEdit.value || isFormDisabled.value,
                      "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => (handleChange($event, _unref(addressSchema).countryId, _unref(countries)))),
                      class: {
                    'is-invalid': !_unref(addressSchema).countryId.valid && _unref(errors)?.countryId,
                  }
                    }), {
                      "default-option": _withCtx(() => [...(_cache[16] || (_cache[16] = [
                        _createTextVNode(" Country ", -1)
                      ]))]),
                      errors: _withCtx(() => [
                        _withDirectives(_createVNode(_unref(ErrorMessage), {
                          name: "countryId",
                          class: "error"
                        }, null, 512), [
                          [_vShow, _unref(mode) !== 'view']
                        ])
                      ]),
                      _: 1
                    }, 16, ["modelValue", "schema", "options", "disabled", "class"])
                  ]),
                  _: 1
                }, 8, ["value"])
              ])
            ])
          ], 512), [
            [_vShow, _unref(showFields)]
          ])
        ]),
        _: 1
      }, 8, ["is-visible"]),
      (_unref(mode) === _unref(FormModes).add || _unref(mode) === _unref(FormModes).edit)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: "footer",
            style: {"display":"block"},
            disabled: !canEdit.value || isFormDisabled.value
          }, [
            _createVNode(NextButton, {
              class: "sc-button",
              text: "Save Address",
              onClick: confirmChanges,
              disabled: _unref(savingChanges),
              loading: _unref(savingChanges)
            }, null, 8, ["disabled", "loading"])
          ], 8, _hoisted_21))
        : _createCommentVNode("", true)
    ])
  ], 2))
}
}

})